import type { Evaluation } from '@compass/client/types/evaluation';
import type { FeedbackInfo } from '@compass/client/types/feedback';
import { CircleAlert } from 'lucide-react';
import React from 'react';

import { Badge } from '@/components/ui/badge';
import { formatAggregateScore } from '@/pages/qc/utils';
import { cn } from '@/utils';

import { getQcScoreLabel, getQcScoreToneClasses, type QcReviewAlert } from '../qcDetail.utils';
import { QcFeedbackPopover } from './QcFeedbackPopover';
import { QcReviewAlerts } from './QcReviewAlerts';
import { QcSectionScoreCards } from './QcSectionScoreCards';

const EMPTY_VALUE = '--';

const getDisplayScore = (score?: number) => {
  return score === undefined || score === null ? EMPTY_VALUE : formatAggregateScore(score);
};

interface QcScorecardSectionCard {
  title: string;
  score?: number;
  scoredCount: number;
  sectionValue: string;
}

interface QcScorecardHeaderProps {
  action?: React.ReactNode;
  alerts: QcReviewAlert[];
  compactHeaderRef: React.Ref<HTMLDivElement>;
  evaluation: Evaluation;
  evaluationFeedbacks?: FeedbackInfo[];
  isCompact: boolean;
  onScrollToSection: (sectionValue: string) => void;
  sectionCards: QcScorecardSectionCard[];
  userNameById?: Map<string, string>;
}

export const QcScorecardHeader: React.FC<QcScorecardHeaderProps> = ({
  action,
  alerts,
  compactHeaderRef,
  evaluation,
  evaluationFeedbacks,
  isCompact,
  onScrollToSection,
  sectionCards,
  userNameById,
}) => {
  const scoreLabel = getQcScoreLabel(evaluation.aggregate_score);

  return (
    <>
      <div
        className="sticky top-0 z-20 h-0 overflow-visible"
        {...(!isCompact ? ({ inert: '' } as React.HTMLAttributes<HTMLDivElement>) : {})}
      >
        <div
          ref={compactHeaderRef}
          aria-hidden={!isCompact}
          className={cn(
            'overflow-hidden border-b bg-background/95 shadow-xs backdrop-blur supports-backdrop-filter:bg-background/80 transition-[opacity,transform] duration-200 ease-out',
            isCompact
              ? 'pointer-events-auto translate-y-0 opacity-100'
              : 'pointer-events-none -translate-y-2 opacity-0',
          )}
        >
          <div className="flex flex-wrap items-center gap-2 px-4 py-2">
            <span
              className={`text-lg font-bold ${getQcScoreToneClasses(evaluation.aggregate_score)}`}
            >
              {getDisplayScore(evaluation.aggregate_score)}
            </span>
            <Badge variant="secondary" className={cn(scoreLabel.toneClass, 'shrink-0')}>
              {scoreLabel.content}
            </Badge>

            {alerts.map(alert => (
              <span
                key={alert.type}
                className={cn(
                  'inline-flex shrink-0 cursor-default items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium',
                  alert.type === 'compliance'
                    ? 'border-red-200 bg-red-50 text-red-700'
                    : 'border-orange-200 bg-orange-50 text-orange-800',
                )}
                title={alert.description}
              >
                <CircleAlert className="h-3 w-3 shrink-0" />
                {alert.title}
              </span>
            ))}

            <div className="ml-auto flex items-center gap-2">
              {action}
              {evaluation.id && (
                <QcFeedbackPopover
                  evaluationId={evaluation.id}
                  feedback={evaluationFeedbacks}
                  userNameById={userNameById}
                />
              )}
            </div>
          </div>

          {sectionCards.length > 0 && (
            <div className="flex items-center divide-x border-t">
              {sectionCards.map(section => (
                <button
                  key={section.sectionValue}
                  type="button"
                  className="flex cursor-pointer items-center gap-1.5 px-4 py-1.5 hover:bg-muted/50"
                  onClick={() => onScrollToSection(section.sectionValue)}
                >
                  <span className="text-xs text-muted-foreground">{section.title}</span>
                  <span
                    className={cn('text-xs font-semibold', getQcScoreToneClasses(section.score))}
                  >
                    {getDisplayScore(section.score)}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div>
        <div className="px-4 pt-4 pb-3">
          <div className="rounded-md border bg-background">
            <div className="flex items-center gap-3 px-4 py-3">
              <span
                className={`text-2xl font-bold ${getQcScoreToneClasses(evaluation.aggregate_score)}`}
              >
                {getDisplayScore(evaluation.aggregate_score)}
              </span>
              <Badge variant="secondary" className={cn(scoreLabel.toneClass, 'mr-auto')}>
                {scoreLabel.content}
              </Badge>
              {action}
              {evaluation.id && (
                <QcFeedbackPopover
                  evaluationId={evaluation.id}
                  feedback={evaluationFeedbacks}
                  userNameById={userNameById}
                />
              )}
            </div>

            <QcReviewAlerts evaluation={evaluation} />

            {sectionCards.length > 0 && (
              <div className="border-t px-4 py-3">
                <QcSectionScoreCards sections={sectionCards} onSectionClick={onScrollToSection} />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
